import { CommonModule } from '@angular/common';
import { Component, computed, inject, Input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAbstractFilterChip, GalaxyFilterValueInterface } from '@vendasta/galaxy/filter/chips';
import { switchMap } from 'rxjs/operators';
import { CrmInjectionToken, SimplifiedUser, UserService } from '../../../tokens-and-interfaces';
import { tap } from 'rxjs';

@Component({
  selector: 'crm-team-member-filter-chip',
  template: `
    @if (isLoading() && selectedTeamMemberIds().length > 0) {
      <div class="stencil-shimmer"></div>
    } @else {
      {{ selectedTeamMemberText() }}
    }
  `,
  providers: [],
  styleUrls: ['./team-member-filter-chip.component.scss'],
  imports: [CommonModule, TranslateModule],
})
export class TeamMemberFilterChipComponent implements GalaxyAbstractFilterChip {
  selectedTeamMemberIds = signal<string[]>([]);
  selectedTeamMember = signal<SimplifiedUser[]>([]);
  isLoading = signal(true);

  @Input() set initialValue(value: GalaxyFilterValueInterface[]) {
    this.selectedTeamMemberIds.set(value?.map((v) => v.string) || []);
  }

  selectedTeamMemberText = computed(() => {
    if (this.selectedTeamMember().length === 0) {
      return '';
    }
    const firstSalespersonName = this.selectedTeamMember()[0].displayName || this.selectedTeamMemberIds()[0];
    if (this.selectedTeamMember().length === 1) {
      return firstSalespersonName;
    }
    return `${firstSalespersonName} +${this.selectedTeamMember().length - 1}`;
  });

  private readonly config = inject(CrmInjectionToken);
  private readonly userService: UserService;

  constructor() {
    if (this.config.services?.userService === undefined) {
      throw new Error('UserService is not provided');
    }
    this.userService = this.config.services?.userService;

    toObservable(this.selectedTeamMemberIds)
      .pipe(
        tap(() => {
          this.isLoading.set(true);
        }),
        switchMap((userIds) => {
          return this.userService.getMultiUsers(userIds);
        }),
        tap(() => {
          this.isLoading.set(false);
        }),
        takeUntilDestroyed(),
      )
      .subscribe((result) => {
        this.selectedTeamMember.set(result);
      });
  }
}
